import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { tokens, Link } from '@hotjar/hotsauce';

import { ErrorMessage } from '@hotjar/webapp/components/Message';
import { Radio } from '@hotjar/webapp/components/Radio';
import {
  PaymentIcon,
  SUPPORTED_CARDS_ICONS,
} from '@hotjar/webapp/components/PaymentIcons/PaymentIcons';
import { Icon } from '@hotjar/webapp/components/Icon';
import { Tooltip } from '@hotjar/webapp/components/Tooltip';
import { Input } from '@hotjar/webapp/components/Input';
import payPal from '@hotjar/shared-assets/images/paypal-small.svg';
import Arrow from '@hotjar/shared-assets/images/select-arrow.png';
import ArrowFocus from '@hotjar/shared-assets/images/select-arrow-focus.png';

import type { State } from './Reducer';

export interface PaymentDetailsProps {
  state: State;
  // FIXME: This prop appears to be unused.
  // eslint-disable-next-line react/no-unused-prop-types
  paymentsLoading: boolean;
  onPaymentTypeChanged: (paymentMethod: string) => void;
  onVerificationsFieldsChanged: (obj: State['verificationFields']) => void;
  onPaypalLogout: () => void;
  countries: { value: string; label: string }[];
}

type TFunction = (x: string) => string;

const PaymentMethods = styled.div`
  display: flex;
  padding: 16px 0;
`;

const RadioLabel = styled.div`
  padding: 0px 0px 0px 25px;
  font-weight: 600;
  font-size: 14px;
  line-height: 19px;
  margin-right: 32px;
`;

// Helper component to show/hide components but allowing them to be mounted.
// This is required as until the credit-card / paypal fields are mounted, we
// can't initialize the hosted fields.
const DisplaySection = styled.div<{ showDiv: boolean }>`
  display: ${({ showDiv }) => (showDiv ? 'unset' : 'none')};
`;

const Paypal = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 8px;
  background-color: ${tokens.color.bg_active};
`;

const PaypalLogo = styled.img`
  height: 15px;
`;

const StyledIcon = styled(Icon)`
  margin-left: 2px;
`;

const PaymentIcons = styled.span`
  height: 52px;
  width: 190px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  > *:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  > *:not(:last-child) {
    margin-right: 16px;
  }
`;

const FlexItem = styled.div``;

const Label = styled.label`
  font-weight: 600;
  font-size: 11px;
  line-height: 80%;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const Heading = styled.span`
  font-size: 14px;
  font-weight: 600;
`;

const translateWithPrefix = (t: TFunction) => (prefix: string) => (key: string) =>
  t(`${prefix}.${key}`);

// Generic styles to be used with any input or input-like element.
const inputStyles = css`
  outline: none;
  color: ${tokens.color.text_default};
  width: 100%;
  height: 40px;
  background: ${tokens.color.ui_background};
  padding: 0px 12px;
  border: 1px solid ${tokens.color.ui_border};
  border-radius: 2px;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.12);
  font-size: 14px;
  line-height: 1.42857143;

  &[disabled] {
    background-color: ${tokens.color.ui_disabled};
    border: 1px solid ${tokens.color.ui_disabled};
    color: ${tokens.color.text_default};
    box-shadow: none;
    cursor: not-allowed;
    opacity: 1;
  }
`;

// How the input style changes when there's an error to be highlighted.
const inputErrorStyles = css`
  border-color: ${tokens.color.ui_border_danger};
  border-left-width: 3px;

  &[disabled] {
    border-color: ${tokens.color.ui_border_danger};
    border-left-width: 3px;
  }
`;

// If there's an error message to be shown along-side highlighting the input
// element, style the message using these styles.
const errorMessageStyles = css`
  color: ${tokens.color.text_danger};
  margin-top: 4px;
  font-size: 12px;
`;

type HostedFieldProps = {
  id: string;
  isError: boolean;
  errorMessage?: string | undefined;
};

// Hosted field element wrapped with error highlighting and messaging.
const HostedFieldInput: FunctionComponent<HostedFieldProps> = ({ id, isError, errorMessage }) => (
  <div>
    <div id={id} css={[inputStyles, isError && inputErrorStyles]} />
    {isError && errorMessage && <div css={errorMessageStyles}>{errorMessage}</div>}
  </div>
);

// Custom styles for the 'month' part of a card's expiration date, as the
// expiration date is 2 inputs masquerading as one.
const monthStyles = css`
  width: 50%;

  position: relative;
  padding: 0 15px;
  border-radius: 2px 0 0 2px;

  :before {
    content: '/';
    position: absolute;
    top: 0;
    right: -5px;
    width: 10px;
    height: 38px;
    background: ${tokens.color.ui_background};
    font-size: 20px;
    font-weight: 300;
    color: ${tokens.color.text_disabled};
    text-align: center;
    padding-top: 6px;
  }
`;

// Custom styles for the 'year' part of a card's expiration date, as the
// expiration date is 2 inputs masquerading as one.
const yearStyles = css`
  width: 50%;
  padding: 0 15px;
  border-radius: 0 2px 2px 0;
`;

type ExpiryDateHostedFieldsInputProps = {
  id: string;
  isError: boolean;
  errorMessage?: string | undefined;
};

// Hosted fields for a card's expiry date, wrapped with error highlighting and
// messaging
const ExpiryDateHostedFieldsInput: FunctionComponent<ExpiryDateHostedFieldsInputProps> = ({
  id,
  isError,
  errorMessage,
}) => (
  <div>
    <div style={{ display: 'flex' }}>
      <div id={`${id}-month`} css={[inputStyles, monthStyles, isError && inputErrorStyles]} />
      <div id={`${id}-year`} css={[inputStyles, yearStyles, isError && inputErrorStyles]} />
    </div>
    {isError && errorMessage && <div css={errorMessageStyles}>{errorMessage}</div>}
  </div>
);

// Custom styling for <select> elements to match other <input> elements.
const selectStyles = css`
  padding-right: 34px;

  appearance: none;

  background: ${tokens.color.white} none no-repeat;
  background-image: url(${Arrow});
  background-size: 34px;
  background-position: right center;

  :focus {
    background-image: url(${ArrowFocus});
  }
`;

type SelectInputProps = {
  isError: boolean;
  errorMessage?: string | undefined;
};

// Select element wrapped with error highlighting and messaging
const SelectInput: FunctionComponent<SelectInputProps & React.HTMLProps<HTMLSelectElement>> = ({
  isError,
  errorMessage,
  children,
  ...props
}) => (
  <div>
    <select css={[inputStyles, selectStyles, isError && inputErrorStyles]} {...props}>
      {children}
    </select>
    {isError && errorMessage && <div css={errorMessageStyles}>{errorMessage}</div>}
  </div>
);

// A component to ensure external links always have the right `target` and `rel`
// props for safety.
const ExternalLink: FunctionComponent<React.HTMLProps<HTMLAnchorElement>> = ({
  children,
  ...props
}) => (
  <a {...props} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

/**
 * A component to add payment details
 */
export const AddPayment = ({
  state,
  onPaymentTypeChanged,
  onVerificationsFieldsChanged,
  countries,
  onPaypalLogout,
}: PaymentDetailsProps): JSX.Element => {
  const { t } = useTranslation('common');
  const trans = translateWithPrefix(t)('yearly-upsell.upgrade-summary.payment-method');

  const cvvToolTip = trans('cvv');
  const addressToolTip = trans('address');

  const onChangeToCreditCard = useCallback(
    () => onPaymentTypeChanged('credit_card'),
    [onPaymentTypeChanged],
  );

  const onChangeToPayPal = useCallback(
    () => onPaymentTypeChanged('paypal'),
    [onPaymentTypeChanged],
  );

  return (
    <>
      <PaymentMethods>
        <Radio
          id="credit"
          name="credit_card"
          value="credit"
          checked={state.paymentType === 'credit_card'}
          onChange={onChangeToCreditCard}
          disabled={false}
          variant="checkbox"
        >
          <RadioLabel>Credit card</RadioLabel>
        </Radio>
        <Radio
          id="ppal"
          name="paypal"
          value="paypal"
          checked={state.paymentType === 'paypal'}
          onChange={onChangeToPayPal}
          disabled={false}
          variant="checkbox"
        >
          <RadioLabel>
            <span className="icon-paypal" />
          </RadioLabel>
        </Radio>
      </PaymentMethods>
      <DisplaySection showDiv={state.paymentType === 'credit_card'}>
        <form id="credit-card-fields" autoComplete="off">
          <FlexContainer>
            {!state.cardTypeAccepted && (
              <ErrorMessage>
                We do not accept {state.cardType} credit cards yet. Please use{' '}
                {/* FIXME: convert this to a button - but watch for styling changes. */}
                {/* FIXME: jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events */}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events */}
                <a role="button" tabIndex={0} onClick={onChangeToPayPal}>
                  PayPal
                </a>{' '}
                instead or{' '}
                <ExternalLink href="https://www.hotjar.com/contact">Contact&nbsp;us</ExternalLink>{' '}
                for assistance.
              </ErrorMessage>
            )}
            <FlexRow>
              <FlexItem style={{ flexGrow: 1 }}>
                <Label htmlFor="card-name">Name on card</Label>
                <Input
                  type="text"
                  id="card-name"
                  name="card-name"
                  maxLength={250}
                  placeholder="e.g. John Doe"
                  value={state.verificationFields.givenName}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    onVerificationsFieldsChanged({
                      ...state.verificationFields,
                      givenName: event.target.value,
                    });
                  }}
                  isError={!!state.fieldErrors.givenName}
                  errorMessage="Enter the card holder name"
                  size={undefined}
                  inputRef={undefined}
                />
              </FlexItem>
              <FlexItem>
                <PaymentIcons>
                  {SUPPORTED_CARDS_ICONS.map((payment) => (
                    <PaymentIcon key={payment} name={payment} alt={payment} />
                  ))}
                </PaymentIcons>
              </FlexItem>
            </FlexRow>
            <FlexRow>
              <FlexItem>
                <Label htmlFor="credit-card-fields-form-number">Card number</Label>
                <HostedFieldInput
                  id="credit-card-fields-number"
                  isError={state.fieldErrors.number}
                  errorMessage="Invalid card number."
                />
              </FlexItem>
              {/* <!-- EXPIRATION UNTIL --> */}
              <FlexItem style={{ width: '130px' }}>
                <Label htmlFor="credit-card-fields-form-expiration-month">Expiration Date</Label>
                <ExpiryDateHostedFieldsInput
                  id="credit-card-fields-expiration"
                  isError={state.fieldErrors.expirationMonth || state.fieldErrors.expirationYear}
                  errorMessage="Invalid date."
                />
              </FlexItem>
              {/* <!-- CVC --> */}
              <FlexItem style={{ width: '60px' }}>
                <Label htmlFor="credit-card-fields-cvv">
                  CVC
                  <Tooltip content={cvvToolTip} appendTo="parent">
                    {/* FIXME: no-noninteractive-tabindex. */}
                    <span // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                      tabIndex={0}
                    >
                      <StyledIcon icon="info" size={10} color={tokens.color.neutral_700} />
                    </span>
                  </Tooltip>
                </Label>
                <HostedFieldInput
                  id="credit-card-fields-cvv"
                  isError={state.fieldErrors.cvv}
                  errorMessage="Invalid CVC."
                />
              </FlexItem>
            </FlexRow>
            <FlexRow>
              <Heading>
                Card billing address:&nbsp;
                <Tooltip content={addressToolTip} appendTo="parent">
                  {/* FIXME: no-noninteractive-tabindex. */}
                  <span
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                    tabIndex={0}
                  >
                    <StyledIcon icon="info" size={16} color={tokens.color.neutral_700} />
                  </span>
                </Tooltip>
              </Heading>
            </FlexRow>
            <FlexRow>
              {/* <!-- Country --> */}
              <FlexItem>
                <Label htmlFor="card-country">Country</Label>
                <SelectInput
                  id="card-country"
                  name="card-country"
                  value={state.verificationFields.countryCodeAlpha2}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    onVerificationsFieldsChanged({
                      ...state.verificationFields,
                      countryCodeAlpha2: event.target.value,
                    });
                  }}
                  isError={state.fieldErrors.countryCodeAlpha2}
                  errorMessage="Field required."
                >
                  <option value="">Please select...</option>
                  {countries.map(({ label, value }) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </SelectInput>
              </FlexItem>

              {/* <!-- City / Town --> */}
              <FlexItem>
                <Label htmlFor="card-city">City / Town</Label>
                <Input
                  type="text"
                  id="card-city"
                  name="card-city"
                  maxLength={250}
                  value={state.verificationFields.locality}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    onVerificationsFieldsChanged({
                      ...state.verificationFields,
                      locality: event.target.value,
                    });
                  }}
                  isError={!!state.fieldErrors.locality}
                  errorMessage="Enter city name"
                  size={undefined}
                  inputRef={undefined}
                />
              </FlexItem>

              {/* <!-- ZIP / Postal code --> */}
              <FlexItem>
                <Label htmlFor="card-post-code">ZIP / Postal code</Label>
                <Input
                  type="text"
                  id="card-post-code"
                  name="card-post-code"
                  maxLength={250}
                  value={state.verificationFields.postalCode}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    onVerificationsFieldsChanged({
                      ...state.verificationFields,
                      postalCode: event.target.value,
                    });
                  }}
                  isError={!!state.fieldErrors.postalCode}
                  errorMessage="Enter postal Code"
                  size={undefined}
                  inputRef={undefined}
                />
              </FlexItem>
            </FlexRow>
          </FlexContainer>
        </form>
      </DisplaySection>
      <DisplaySection showDiv={state.paymentType === 'paypal'}>
        {state.paymentType === 'paypal' && state.isPaypalLoggedIn && (
          <Paypal>
            <span className="email ellipsis">
              <PaypalLogo src={payPal} alt="paypal" />
              &nbsp;&nbsp;
              <b>Paypal</b>&nbsp;
              {state.paypalToken.details.email}
            </span>
            <Link as="button" kind="inline" onClick={onPaypalLogout}>
              Logout
            </Link>
          </Paypal>
        )}
      </DisplaySection>
    </>
  );
};
